import { DiscoveryApi, IdentityApi } from '@backstage/core-plugin-api';
import { AgentgatewayApiError } from './errors';
import type { AgentgatewayKeysApi } from './AgentgatewayKeysApi';
import type {
  BudgetKeysResetResponse,
  BudgetResponse,
  BudgetWindowResponse,
  GatewayConfig,
  KeyActionResponse,
  KeyMetadata,
  ModelsResponse,
  SharedConfigResponse,
  UsageInsightsResponse,
  UsageResponse,
} from './types';

/**
 * Production {@link AgentgatewayKeysApi} client that talks to the
 * `agentgateway-keys` backend plugin over the Backstage API proxy.
 */
export class AgentgatewayKeysClient implements AgentgatewayKeysApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly identityApi: IdentityApi;

  constructor(options: {
    discoveryApi: DiscoveryApi;
    identityApi: IdentityApi;
  }) {
    this.discoveryApi = options.discoveryApi;
    this.identityApi = options.identityApi;
  }

  private async api<T>(path: string, init?: RequestInit): Promise<T> {
    const baseUrl = await this.discoveryApi.getBaseUrl('agentgateway-keys');
    const { token } = await this.identityApi.getCredentials();

    const headers: HeadersInit = new Headers(init?.headers);
    if (!headers.has('content-type')) {
      headers.set('content-type', 'application/json');
    }
    if (token && !headers.has('authorization')) {
      headers.set('authorization', `Bearer ${token}`);
    }

    const response = await fetch(`${baseUrl}${path}`, {
      ...init,
      headers,
    });

    if (!response.ok) {
      throw await AgentgatewayApiError.fromResponse(response);
    }

    if (response.status === 204) {
      return undefined as T;
    }
    return response.json() as Promise<T>;
  }

  async getKey(): Promise<KeyMetadata | null> {
    try {
      return await this.api<KeyMetadata>('/key');
    } catch (err: any) {
      if (err?.status === 404 || err?.response?.status === 404) {
        return null;
      }
      throw err;
    }
  }

  createKey(): Promise<KeyActionResponse> {
    return this.api<KeyActionResponse>('/key', { method: 'POST' });
  }

  rotateKey(): Promise<KeyActionResponse> {
    return this.api<KeyActionResponse>('/key/rotate', { method: 'POST' });
  }

  revokeKey(): Promise<void> {
    return this.api<void>('/key', { method: 'DELETE' });
  }

  getUsage(days: number): Promise<UsageResponse> {
    return this.api<UsageResponse>(`/usage?days=${encodeURIComponent(days)}`);
  }

  getUsageInsights(days: number): Promise<UsageInsightsResponse> {
    return this.api<UsageInsightsResponse>(
      `/usage/insights?days=${encodeURIComponent(days)}`,
    );
  }

  getBudget(): Promise<BudgetResponse> {
    return this.api<BudgetResponse>('/budget');
  }

  getBudgetWindow(): Promise<BudgetWindowResponse> {
    return this.api<BudgetWindowResponse>('/budget/window');
  }

  resetBudgetCache(): Promise<BudgetKeysResetResponse> {
    return this.api<BudgetKeysResetResponse>('/budget/keys', {
      method: 'DELETE',
    });
  }

  getConfig(): Promise<GatewayConfig> {
    return this.api<GatewayConfig>('/config');
  }

  getModels(): Promise<ModelsResponse> {
    return this.api<ModelsResponse>('/models');
  }

  getSharedConfig(): Promise<SharedConfigResponse> {
    return this.api<SharedConfigResponse>('/shared-config');
  }

  getVscodeSharedConfig(): Promise<SharedConfigResponse> {
    return this.api<SharedConfigResponse>('/shared-config/vscode');
  }
}
